import { AlertCircle, Shield } from 'lucide-react';

import { Card, Spinner } from '@design-system';
import { ContactSupportButton } from '@/components/ContactSupportButton';

export function QuotesLoadingState({
  title = 'Loading quotes based on your preferences...',
}: {
  title?: string;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 py-16">
      <Spinner className="size-8" />
      <p className="text-gray-500">{title}</p>
    </div>
  );
}

export function QuotesErrorState() {
  return (
    <Card className="mx-auto max-w-md px-6 py-5 text-center">
      <div className="flex flex-col items-center gap-2">
        <AlertCircle className="size-6 text-gray-300" />
        <h3 className="text-base font-medium text-gray-900">Unable to load quotes</h3>
        <p className="text-sm text-gray-500">
          We encountered an issue while loading your quotes. Please try again or contact support if
          the problem persists.
        </p>
        <ContactSupportButton
          subject="Issue loading quotes in guided planning"
          className="mt-1"
        />
      </div>
    </Card>
  );
}

interface QuotesEmptyStateProps {
  reason?: GuidedPlanningNoQuotesReason;
  filterPanel?: React.ReactNode;
}

const FILTER_RELATED_REASONS: (GuidedPlanningNoQuotesReason | 'default')[] = [
  'no_policy_type_match',
  'no_preference_match',
  'default',
];

const EMPTY_STATE_CONTENT: Record<
  GuidedPlanningNoQuotesReason | 'default',
  { title: string; description: string }
> = {
  state_not_supported: {
    title: 'Insurance not available in your state',
    description:
      "Unfortunately, we don't currently have insurance products available in your state. We're working to expand coverage—please check back soon or contact support for more information.",
  },
  age_out_of_range: {
    title: 'Age outside eligible range',
    description:
      'The available insurance products require applicants to be within a specific age range. Please contact your advisor for alternative options.',
  },
  demographics_incomplete: {
    title: 'No quotes available',
    description: 'We were unable to find any quotes based on your current profile.',
  },
  no_demographics_match: {
    title: 'No quotes available for your profile',
    description:
      "We don't currently have quotes matching your profile. We're constantly adding new carriers and products—check back soon or contact support for more information.",
  },
  no_policy_type_match: {
    title: 'No quotes match your selections',
    description: 'Try broadening your selection to include more policy types.',
  },
  no_carrier_match: {
    title: 'Contact your advisor for assistance.',
    description: 'Qualifying policies are not showing based on advisor carrier settings.',
  },
  no_preference_match: {
    title: 'No quotes match your preferences',
    description: 'Try adjusting your payment or benefit structure preferences to see more options.',
  },
  default: {
    title: 'No quotes available',
    description:
      "We couldn't find any quotes matching your criteria. Try broadening your policy type selection.",
  },
};

export function QuotesEmptyState({ reason, filterPanel }: QuotesEmptyStateProps) {
  const content = reason ? EMPTY_STATE_CONTENT[reason] : EMPTY_STATE_CONTENT.default;
  const showFilterPanel =
    filterPanel && (!reason || FILTER_RELATED_REASONS.includes(reason));

  return (
    <div className="mx-auto max-w-lg py-12 text-center">
      <Shield className="mx-auto mb-4 size-12 text-gray-300" />
      <h3 className="text-lg font-medium text-gray-900">{content.title}</h3>
      <p className="mt-2 text-gray-500">{content.description}</p>
      {showFilterPanel && <div className="mt-4 flex justify-center">{filterPanel}</div>}
    </div>
  );
}
